import React, { useState, useEffect, useContext } from "react";
import { Stack, TextField, TooltipHost, Checkbox, PrimaryButton, DirectionalHint, IconButton, Slider, Dropdown, ThemeProvider } from "@fluentui/react";
import { toast } from "react-toastify";
import { ImgMapKeys, genQuestionsLangOptions, imgMap, nmeMap, toolTipConfTexts } from "../../lsts";
import { glineconfig, gselectedgroup, gspoconfig, SaveOptions, empty_gen_opt } from "../../interfaces";
import { GlobalContext } from "../../GlobalContext"; // tests, context accross routes (ex. chat/search)
import styles from "./SpaceEditForm.module.css";
import ImageChanger from "../ImageChanger/ImageChanger";
import TagInput from "../TagInput/TagInput";
import ButtonHold from "../../components/ButtonHold/ButtonHold";
import { assistantCardItems, multiPurposeAssistantCreationData, convConfToolStrUnvoice, convConfToolStrNative, convoLangWavOptions } from "../../lsts";
import {
    stackStyles,
    sliderStyles,
    tooltipStyles,
    dropdownStyles,
    textFieldStyles,
    textSPOFieldStyles,
    txtSpaceGenEditStyles,
    dropdownStylesGenAiQs,
    invFieldConfMainSaveButStyles,
    textFieldMutlilineGreetingStyles,
    textFieldStylesMultilineSpaceEdit
} from "../../styles_glob";
import customTheme from "./../../theme/customTheme";
import { resultCountOptions, convoLangOptions, chatbotScript, chatbotEmbed, assistantCodes, principleName, principleId } from "./../../lsts";
import { sync_spo_content, upsertGroup } from "../../api";

interface ISpaceEditFormProps {
    selectedGroup: gselectedgroup;
    onEditSpaceSave: () => void;
    onGroupDel: () => void;
}

// options to save immediately on change

const SpaceEditForm: React.FC<ISpaceEditFormProps> = ({ selectedGroup, onEditSpaceSave, onGroupDel }) => {
    const { loggedInUser, activeComp }      = useContext(GlobalContext);
    const [editedSpace, setEditedSpace]     = useState<gselectedgroup>({ ...selectedGroup });
    const [isLoadingAPI, setIsLoadingAPI]   = useState(false);
    const [apiKState, setApiKState]         = useState("");
    const [shareURL, setShareURL]           = useState("");
    const [anonSharelink, setAnonSharelink] = useState(false);

    useEffect(() => {
        setEditedSpace({ ...selectedGroup });
        handleShareURL(anonSharelink);
    }, [selectedGroup]);

    useEffect(() => {
        setIsLoadingAPI(false);
    }, []);

    const getHeader = () => {
        return {
            ...(loggedInUser?.token ? { Authorization: `Bearer ${loggedInUser.token}` } : {}),
            "X-MS-CLIENT-PRINCIPAL-NAME": loggedInUser?.mail ? loggedInUser.mail : "",
            "X-MS-CLIENT-PRINCIPAL-ID": loggedInUser?.userId ? loggedInUser.userId : "",
            "Content-Type": "application/json"
        };
    };

    const handleChange = (field: keyof gselectedgroup, value: string | number | boolean) => {
        setEditedSpace(prev => ({ ...prev, [field]: value }));
        if (field === "sharewithcode") {
            handleShareURL(anonSharelink);
            handleSave({
                sharewithcode: value as boolean
            });
        }
    };


    const handleChangeGenOpt = (field: string, value: any) => {
        setEditedSpace(prev => {
            const prevGenOpt = prev.gen_opt ? prev?.gen_opt || empty_gen_opt : empty_gen_opt;
            const newGenOpt  = { ...prevGenOpt, [field]: value };
            return { ...prev, gen_opt: newGenOpt };
        });
    };

    const handleSPOChange = (field: keyof gspoconfig, value: Partial<gspoconfig>) => {
        setEditedSpace(prev => {
            return { ...prev, spo_config: { ...prev.spo_config, ...value } };
        });
    };

    const handleLineChange = (field: keyof glineconfig, value: Partial<glineconfig>) => {
        setEditedSpace(prev => {
            return { ...prev, line_config: { ...prev.line_config, ...value } };
        });
    };

    const handleShareURL = (anon: boolean) => {
        let sUrl = `${window.location.origin}?redeemCode=${encodeURIComponent(selectedGroup.selectionId)}&spt=${encodeURIComponent(selectedGroup.selectionText)}`;
        if (anon) sUrl = `${sUrl}&A=1`;
        setShareURL(sUrl);
    };

    const handleAnonChange = (checked: boolean) => {
        setAnonSharelink(checked);
        handleShareURL(checked);
    };

    const handleSave = (instaOptions: SaveOptions) => {
        setIsLoadingAPI(true);
        upsertGroup(getHeader(), editedSpace, instaOptions).then(() => {
            setIsLoadingAPI(false);
            onEditSpaceSave();
        });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {})
            .catch(err => {});
        toast.success("Copied to Clipboard");
    };

    const delSpace = async () => {
        const groupid = selectedGroup.selectionId;
        fetch("/qDelGroup", {
            method: "POST",
            headers: getHeader(),
            body: JSON.stringify({ groupid: groupid })
        }).then(Response => {
            onGroupDel();
        });
    };

    const handleDelete = () => {
        const userConfirmed = window.confirm("Are you sure you want to delete this space?");
        if (userConfirmed) {
            delSpace();
        }
    };

    const qGetK = () => {
        const params = new URLSearchParams({ groupid: selectedGroup.selectionId });
        const url = "/qGetK" + `?${params.toString()}`;
        fetch(url, {
            method: "GET",
            headers: getHeader()
        }).then((res: any) => {
            if (res.ok) {
                const apik_arr = res.json().then((data: any) => {
                    if (data && data.length > 0) {
                        const apikey = data[0]?.apikey;
                        setApiKState(apikey);
                    }
                });
            }
        });
    };

    const onImageChange = (imageUrl: string) => {
        setEditedSpace(prev => ({ ...prev, headerimageurl: imageUrl }));
        handleSave({
            headerimageurl: imageUrl
        });
    };

    const onHardDefinedQuestionsChange = (tags: string[]) => {
        setEditedSpace(prev => ({ ...prev, manualquestionsuggestions: tags.join(";") }));
    };

    return (
        <div className={styles.editModalWrap}>
            <ThemeProvider theme={customTheme}>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>General (Name, Image, Description, Tab Availability)</span>
                    </div>
                    <div className={styles.custLabelMrgn}>
                        <span>Name & Description</span>
                    </div>
                    <div className={styles.editFormNameDescFlex}>
                        <TextField
                            className={styles.spaceTextFieldsStackGen}
                            styles={txtSpaceGenEditStyles}
                            label="Space Name"
                            value={editedSpace.selectionText}
                            onChange={(_, newValue) => handleChange("selectionText", newValue || "")}
                        />
                        <TextField
                            className={styles.spaceTextFieldsStackGen}
                            styles={txtSpaceGenEditStyles}
                            label="Space Description"
                            value={editedSpace.description}
                            onChange={(_, newValue) => handleChange("description", newValue || "")}
                        />

                        <TextField
                            className={styles.spaceTextFieldsStackGen}
                            styles={txtSpaceGenEditStyles}
                            label="Assistant Type"
                            disabled={true}
                            value={`${nmeMap[(editedSpace?.assistanttype ?? "anya") as ImgMapKeys]} (${editedSpace?.assistanttype})`}
                        />

                    </div>

                    <div className={styles.custLabelMrgn}>
                        <span>Change Assistant Card Image</span>
                    </div>
                    <div>
                        <ImageChanger
                            defaultImage={imgMap[(editedSpace?.assistanttype ?? "anya") as ImgMapKeys]}
                            customImage={editedSpace?.headerimageurl}
                            onImageChange={onImageChange}
                        />
                    </div>
                </Stack>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>Greeting Configuration</span>
                    </div>

                    <TooltipHost content={toolTipConfTexts["dontloadlastconvo"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Dont Display Last Conversation on Load"
                            disabled={false}
                            checked={editedSpace.dontloadlastconvo == 1}
                            onChange={(_, checked) => handleChange("dontloadlastconvo", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Don't load latest conversation history in the chat tab on load.</div>
                    </TooltipHost>
                    {selectedGroup.assistanttype != assistantCodes.ipa &&
                        <TooltipHost content={toolTipConfTexts["landonmanage"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Checkbox
                                className={styles.editSpaceCheckbox}
                                label="Set Manage Tab as Home"
                                disabled={false}
                                checked={editedSpace.landonmanage == 1}
                                onChange={(_, checked) => handleChange("landonmanage", checked ? 1 : 0)}
                            />
                            <div className={styles.contentOptSubTitleInfo}>Land on manage page when entering the assistant.</div>
                        </TooltipHost>
                    }
                    <TooltipHost content={toolTipConfTexts["custgreetingmessage"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles} className={styles.tooltipEditSpace}>
                        <TextField
                            className={styles.spaceTextFields}
                            styles={textFieldMutlilineGreetingStyles}
                            label="Greeting Message"
                            description="Supports Markdown. Bold: **Bold Text**. Italic: *Italic Text. # for Heading 1. ## for Heading 2. For more then one New Line use <br>. For Bullet Points use - or *"
                            multiline
                            resizable={false}
                            autoAdjustHeight={true}
                            value={
                                editedSpace.custgreetingmessage ||
                                (editedSpace.assistanttype == "anya" || editedSpace.assistanttype == ""
                                    ? multiPurposeAssistantCreationData.custgreetingmessage
                                    : assistantCardItems.filter(item => item.creationData.assistanttype === editedSpace.assistanttype)[0]?.creationData
                                          .custgreetingmessage)
                            }
                            onChange={(_, newValue) => handleChange("custgreetingmessage", newValue || "")}
                        />
                    </TooltipHost>
                    <div className={styles.custLabelTagTwoInput}>
                        <span>Greeting Example Questions</span>
                    </div>
                    <div className={styles.groupFuncQLang}>
                        <TooltipHost content={toolTipConfTexts["showexamplequestionsonstart"]} directionalHint={DirectionalHint.topLeftEdge} calloutProps={tooltipStyles}>
                            <Checkbox
                                className={styles.editSpaceCheckbox}
                                label="Enable AI Generated Question Suggestions in Assistant Greeting Message"
                                disabled={false}
                                checked={editedSpace.showexamplequestionsonstart == 1}
                                onChange={(_, checked) => handleChange("showexamplequestionsonstart", checked ? 1 : 0)}
                            />
                        </TooltipHost>

                        <div className={`${styles.genAiExampleQuestionsFields} ${editedSpace.showexamplequestionsonstart == 1 ? styles.show : styles.hide}`}>
                            {editedSpace.showexamplequestionsonstart == 1 && (
                                <div>
                                    <div className={styles.custLabelTagInputSmall}>
                                        <span>Language of AI Example Questions</span>
                                    </div>
                                    <Dropdown
                                        options={genQuestionsLangOptions}
                                        disabled={false}
                                        selectedKey={editedSpace.aiquestionslang || "auto"}
                                        onChange={(_, option) => handleChange("aiquestionslang", `${option?.key}`)}
                                        styles={dropdownStylesGenAiQs}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.groupFunc}>
                        <TooltipHost content={toolTipConfTexts["manualquestionsuggestions"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <div className={styles.custLabelTagInput}>
                                <span>Add Manual Example Questions in Assistant Greeting</span>
                            </div>
                        </TooltipHost>
                        <TagInput
                            onChange={onHardDefinedQuestionsChange}
                            palceholderText="Add an Example Question and press Enter"
                            initialTags={editedSpace.manualquestionsuggestions != "" ? editedSpace.manualquestionsuggestions.split(";") : []}
                        />
                    </div>
                </Stack>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>Complete Experiences</span>
                    </div>
                    <div className={styles.contentSubHeaderExplainer}>Enabling these experiences alters the fundamentals of how the assistant functions. Consult the documentation for more details.</div>
                    <TooltipHost content={selectedGroup.assistanttype != assistantCodes.cca ?  convConfToolStrUnvoice : convConfToolStrNative} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Autonomous Call Center Assistant"
                            disabled={false}
                            checked={editedSpace.convomode == 1}
                            onChange={(_, checked) => handleChange("convomode", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Enable natural voice conversations, scenario-based interactions, data collections, trainings in a true intelligent ai conversational experience.</div>
                        {/*<div className={styles.betaFeaturesDetails}>Show New Features</div>*/}
                    </TooltipHost>
                    {editedSpace.convomode == 1 && (
                        <TooltipHost content="Enable campaign management for outbound call workflows. Allows creating campaigns, assigning contacts, and filtering scenarios and fields per campaign." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Checkbox
                                className={styles.editSpaceCheckbox}
                                label="Enable Campaigns"
                                disabled={false}
                                checked={editedSpace.campaigns_enabled == 1}
                                onChange={(_, checked) => handleChange("campaigns_enabled", checked ? 1 : 0)}
                            />
                            <div className={styles.contentOptSubTitleInfo}>Enable campaign management for outbound call workflows with contact lists and campaign-specific scenarios.</div>
                        </TooltipHost>
                    )}
                    <TooltipHost content={toolTipConfTexts["viewerscanupload"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Personal Assistant Mode"
                            checked={editedSpace.viewerscanupload == 1}
                            onChange={(_, newValue) => handleChange("viewerscanupload", newValue || 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Convert the assistant into a Company Personal Assistant. Each user has access to only what they upload.</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["hideroottabs"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Pure Content Handling Mode"
                            checked={editedSpace.hideroottabs == 1}
                            onChange={(_, newValue) => handleChange("hideroottabs", newValue || 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>No Conversations Tab, instead options for scheduled AI report generations, file list AI interactions and API integrationns and more.</div>
                    </TooltipHost>
                    {/* audio recognition language - if cca this setting is in special config page, else is in here*/}
                    {selectedGroup.assistanttype != assistantCodes.cca && (
                        <TooltipHost content="Specifying Language Improves Speech Recognition" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Dropdown
                                label="Speech Recognition Default Language"
                                options={convoLangWavOptions}
                                disabled={(editedSpace.convomode == 1 || editedSpace?.allowaudiofiles == 1) ? false : true}
                                selectedKey={editedSpace.convolang || "auto"}
                                onChange={(_, option) => handleChange("convolang", `${option?.key}`)}
                                styles={dropdownStyles}
                            />
                            <div className={styles.contentOptSubTitleInfoNoMargin}>Speech Recognition Default Language</div>
                        </TooltipHost>
                    )}
                </Stack>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>Text Response Features</span>
                    </div>
                    <div className={styles.contentSubHeaderExplainer}>Settings defining the behavior in Text Conversations</div>
                    <TooltipHost content={toolTipConfTexts["questionsuggestions"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Enable Followup Questions"
                            disabled={false}
                            checked={editedSpace.questionsuggestions == 1}
                            onChange={(_, checked) => handleChange("questionsuggestions", checked ? 1 : 0)}
                        />
                        {/*<div className={styles.betaFeaturesDetails}>Show New Features</div>*/}
                        <div className={styles.contentOptSubTitleInfo}>Suggest relevant follow-up questions at the end of each response.</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["disablecitationhighlighting"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Disable Citations"
                            disabled={false}
                            checked={editedSpace.disablecitationhighlighting == 1}
                            onChange={(_, checked) => handleChange("disablecitationhighlighting", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Don't produce and display citation links in conversation responses. [advanced]</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["hideaisearch"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Hide Source Content List"
                            disabled={false}
                            checked={editedSpace.hideaisearch == 1}
                            onChange={(_, checked) => handleChange("hideaisearch", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Don't show information about the considered source content at top of responses</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["enabledocaudio"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Enable Content Audio"
                            disabled={false}
                            checked={editedSpace.enabledocaudio == 1}
                            onChange={(_, checked) => handleChange("enabledocaudio", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Add option to narrate source content</div>
                    </TooltipHost>
                    {((selectedGroup.assistanttype == assistantCodes.dpa || selectedGroup.assistanttype == assistantCodes.ipa) || editedSpace.perchunkmeta == 1)  && (
                        <TooltipHost content={toolTipConfTexts["showchartsinuni"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Checkbox
                                className={styles.editSpaceCheckbox}
                                label="Enable AI Reports & Charting"
                                disabled={false}
                                checked={editedSpace.showchartsinuni == 1}
                                onChange={(_, checked) => handleChange("showchartsinuni", checked ? 1 : 0)}
                            />
                            <div className={styles.contentOptSubTitleInfo}>Produce and display data charts after responses. Content ingested without 'Per Chunk Metadata' On will have to be reingested to be available for enhanced responses and data visualization. [beta]</div>
                        </TooltipHost>
                    )}

                    {
                        editedSpace.showchartsinuni == 1 && (
                            <TooltipHost content={toolTipConfTexts["perchunkmeta"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Checkbox
                                    className={styles.editSpaceCheckbox}
                                    label="Enable Chart display via Mermaid."
                                    disabled={false}
                                    checked={(editedSpace?.gen_opt?.use_mermaid || 0)  == 1}
                                    onChange={(_, checked) => handleChangeGenOpt('use_mermaid', checked ? 1 : 0)}
                                />
                                <div className={styles.contentOptSubTitleInfo}>Enable Chart display via Mermaid. [beta]</div>
                            </TooltipHost>
                        )
                    }

                    <TooltipHost content={toolTipConfTexts["usenewmodel"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Use Latest Model for Responses"
                            disabled={false}
                            checked={editedSpace.usenewmodel == 1}
                            onChange={(_, checked) => handleChange("usenewmodel", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Use latest model for main chat responses.</div>
                    </TooltipHost>
                    <TooltipHost content="Optimized V2 content retrieval. Faster, more intelligent and efficient." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Try our optimized V2 knowledge retrieval"
                            disabled={false}
                            checked={editedSpace.uselatestretrieval == 1}
                            onChange={(_, checked) => handleChange("uselatestretrieval", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Enable V2 content retrieval for conversation responses. Faster, more intelligent and efficient.</div>
                    </TooltipHost>
                </Stack>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>Response Instructions</span>
                    </div>
                    <div className={styles.contentSubHeaderExplainer}>Instructions for Conversational Responses</div>
                    {selectedGroup.assistanttype != assistantCodes.cca && (
                        <TooltipHost content={toolTipConfTexts["custaiinst"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <TextField
                                className={styles.spaceTextFields}
                                styles={textFieldStylesMultilineSpaceEdit}
                                label="AI Instructions"
                                multiline
                                resizable={false}
                                value={
                                    editedSpace.custaiinst ||
                                    (editedSpace.assistanttype == "anya" || editedSpace.assistanttype == ""
                                        ? multiPurposeAssistantCreationData.custaiinst
                                        : assistantCardItems.filter(item => item.creationData.assistanttype === editedSpace.assistanttype)[0]?.creationData
                                              .custaiinst)
                                }
                                onChange={(_, newValue) => handleChange("custaiinst", newValue || "")}
                            />
                        </TooltipHost>
                    )}
                    <TooltipHost content={toolTipConfTexts["inscope"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Allow external knowledge"
                            disabled={false}
                            checked={!(editedSpace.inscope == 1)}
                            onChange={(_, checked) => handleChange("inscope", checked ? 0 : 1)}
                        />
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["temperature"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Slider
                                styles={sliderStyles}
                                label="Creativity"
                                min={0}
                                max={1}
                                step={0.1}
                                defaultValue={editedSpace.temperature}
                                showValue
                                onChange={value => handleChange("temperature", value)}
                            />
                        </TooltipHost>
                    {(selectedGroup.assistanttype == assistantCodes.anya || selectedGroup.assistanttype == assistantCodes.aas || selectedGroup.assistanttype == assistantCodes.pps) && (
                        <div>
                            <div className={styles.editFormSectionTitle}>
                                <span>AI Search Configuration for V1 Chat Assistants</span>
                            </div>
                            <TooltipHost content="The amount of search results to be sent to the AI Model" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Dropdown
                                    label="Search Results Count"
                                    options={resultCountOptions}
                                    selectedKey={editedSpace.retrievecount.toString()}
                                    onChange={(_, option) => handleChange("retrievecount", Number(option?.key))}
                                    styles={dropdownStyles}
                                />
                            </TooltipHost>
                            <TooltipHost content="The amount of relevance needed for a search result to be included in the final results. Higher is more strict leading to less results." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Slider
                                    styles={sliderStyles}
                                    label="Search Relevance Strictness"
                                    min={1}
                                    max={5}
                                    step={1}
                                    defaultValue={editedSpace.strictness}
                                    showValue
                                    onChange={value => handleChange("strictness", value)}
                                />
                            </TooltipHost>
                        </div>
                    )}
                </Stack>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>Content Input</span>
                    </div>
                    <div className={styles.custLabelMrgn}>
                        <span>Additional Conversation Features</span>
                    </div>
                    <TooltipHost content="Enable users to talk with the assistant in real time. This is an additional feature of the text based assistants. Not the same as Call Center Assistants." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Audio Input"
                            disabled={false}
                            checked={editedSpace.allowsimpleconvo == 1}
                            onChange={(_, checked) => handleChange("allowsimpleconvo", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Enable users to talk with the assistant in real time. This is an additional feature of the text based assistants. Not the same as Call Center Assistants.</div>
                    </TooltipHost>
                    <div className={styles.custLabelMrgn}>
                        <span>Content Sources</span>
                    </div>
                    <TooltipHost content="Allow Default Content Types. PDF, Images and some Office Documents. Some might be missing in Free/Demo " directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Default"
                            disabled={true}
                            checked={true}
                            onChange={(_, checked) => handleChange("allowweb", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow common office documents, to be uploaded as content.</div>
                    </TooltipHost>
                    <TooltipHost content="Allow Web Pages to be added as content." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Web Page"
                            disabled={false}
                            checked={editedSpace.allowweb == 1}
                            onChange={(_, checked) => handleChange("allowweb", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow web pages, to be added as content.</div>
                    </TooltipHost>
                    <TooltipHost content="Record meetings in real time and edit/add the transcription as content." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Real-Time Meetings"
                            disabled={false}
                            checked={editedSpace.allowaudiodocentry == 1}
                            onChange={(_, checked) => handleChange("allowaudiodocentry", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Record meetings in real time and use the transcription as content.</div>
                    </TooltipHost>
                    <TooltipHost content="Allows users to define a URL from which Links are to be followed and extracted. The user writes instructions about what it wants extracted" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Web Page Link Extraction"
                            disabled={false}
                            checked={editedSpace.urllinkextraction == 1}
                            onChange={(_, checked) => handleChange("urllinkextraction", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow AI web scraping of web lists.</div>
                    </TooltipHost>
                    <TooltipHost content="Upload Audio Files to be upload as content. (Limited for Free/Demo Version)" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Upload Audio Files (Limited Functionality in Demo)"
                            disabled={false}
                            checked={editedSpace.allowaudiofiles == 1}
                            onChange={(_, checked) => handleChange("allowaudiofiles", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow audio files to be uploaded as content.</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["allowvideofiles"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Video Files (Availability may vary)"
                            disabled={false}
                            checked={editedSpace.allowvideofiles == 1}
                            onChange={(_, checked) => handleChange("allowvideofiles", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow video files to be uploaded as content.</div>
                    </TooltipHost>
                    <TooltipHost content="Allow syncing content from other groups into this group." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Group Connect (Sync from other groups)"
                            disabled={false}
                            checked={editedSpace.allowgroupconnect == 1}
                            onChange={(_, checked) => handleChange("allowgroupconnect", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow syncing content from other groups.</div>
                    </TooltipHost>
                    <TooltipHost
                        content={
                            <div>
                                <span className={styles.tooltipYoutubeNormal}>Enable pure video/audio comprehension.</span>
                                <span className={styles.tooltipYoutubeNormal}>
                                    Structures audio/video content into optical character recognized key frames and speech-to-text and makes them avalable to
                                    the Assistant.
                                </span>{" "}
                                <span className={styles.tooltipYoutubeRed}>
                                    Feature not always available in free/demo version. For production use, please contact us for more information
                                </span>
                            </div>
                        }
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        calloutProps={tooltipStyles}
                    >
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Youtube (Availability may vary)"
                            disabled={true}
                            checked={editedSpace.allowyoutube == 1}
                            onChange={(_, checked) => handleChange("allowyoutube", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Allow youtube videos to be added as content.</div>
                    </TooltipHost>
                    <div className={styles.custLabelMrgn}>
                        <span>Integrations</span>
                    </div>
                    <TooltipHost content="Store integration data in dynamic tables." directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Integration Data in Dynamic Tables"
                            disabled={false}
                            checked={(editedSpace?.gen_opt?.use_dynamic_dbs || 0) == 1}
                            onChange={(_, checked) => handleChangeGenOpt("use_dynamic_dbs", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Enable storing integration data in dynamic tables.</div>
                    </TooltipHost>
                    <TooltipHost content="Setup SharePoint Synchronization" directionalHint={DirectionalHint.rightCenter} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="SharePoint"
                            disabled={false}
                            checked={editedSpace.spo_config.enabled == 1}
                            onChange={(_, checked) => handleSPOChange("enabled", { enabled: checked ? 1 : 0 })}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Setup content synchronization of SharePoint Online Libraries. (limited availability & performance in demo)</div>
                    </TooltipHost>
                    <div className={`${styles.spoFields} ${editedSpace.spo_config.enabled == 1 ? styles.show : styles.hide}`}>
                        {editedSpace.spo_config.enabled == 1 && (
                            <>
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textSPOFieldStyles}
                                    label="Tenant ID"
                                    value={editedSpace.spo_config.tenant_id}
                                    onChange={(_, newValue) => handleSPOChange("tenant_id", { tenant_id: newValue || "" })}
                                />
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textSPOFieldStyles}
                                    label="Full Library URL"
                                    value={editedSpace.spo_config.full_lib_url}
                                    onChange={(_, newValue) => handleSPOChange("full_lib_url", { full_lib_url: newValue || "" })}
                                />
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textSPOFieldStyles}
                                    label="Graph Full URL"
                                    value={editedSpace.spo_config.graph_full_url}
                                    onChange={(_, newValue) => handleSPOChange("graph_full_url", { graph_full_url: newValue || "" })}
                                />
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textSPOFieldStyles}
                                    label="Client ID"
                                    value={editedSpace.spo_config.client_id}
                                    onChange={(_, newValue) => handleSPOChange("client_id", { client_id: newValue || "" })}
                                />
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textSPOFieldStyles}
                                    label="Client Secret"
                                    type="password"
                                    value={editedSpace.spo_config.client_secret}
                                    onChange={(_, newValue) => handleSPOChange("client_secret", { client_secret: newValue || "" })}
                                />

                                <div className={styles.confirmInvoiceButton}>
                                    {selectedGroup.permissionlevel > 1 && (
                                        <div className={styles.confirmSPOSyncButton}>
                                            <ButtonHold
                                                confirmed={false}
                                                confirm={() => {sync_spo_content(selectedGroup, loggedInUser)}}
                                                btext="Sync SP"
                                                btextConfirmed="Syncing"
                                            />
                                            <div className={styles.confirmSPOBelowBorder}></div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <TooltipHost content="Confluence Synchronization. Available upon inquiry" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Confluence"
                            disabled={true}
                            checked={false}
                            onChange={(_, checked) => handleSPOChange("enabled", { enabled: checked ? 1 : 0 })}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Setup content synchronization with Confluence. (availability may vary in demo version)</div>
                    </TooltipHost>
                    <div className={styles.custLabelMrgn}>
                        <span>Advanced AI Data Processing</span>
                    </div>
                    <TooltipHost content={toolTipConfTexts["invoicesgroup"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="AI Data Processing & Automation"
                            disabled={selectedGroup.assistanttype == assistantCodes.ipa ? true : false}
                            checked={editedSpace.invoicesgroup == 1}
                            onChange={(_, checked) => handleChange("invoicesgroup", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Enable advanced processing & automation capabilities for ai structuring, extraction & big data ai reporting.</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["usedocintel"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Doc Intel Availability"
                            disabled={selectedGroup.assistanttype == assistantCodes.ipa ? true : false}
                            checked={editedSpace.usedocintel == 1}
                            onChange={(_, checked) => handleChange("usedocintel", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Enable Doc Intel Content Processing for special usecases & challenging content</div>
                    </TooltipHost>
                    <TooltipHost content={toolTipConfTexts["forceocr"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Force Vision AI Content Processing"
                            disabled={false}
                            checked={editedSpace.forceocr == 1}
                            onChange={(_, checked) => handleChange("forceocr", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Force Vision AI processing for all content irregardles of existing text. Intended for special cases with irregular content.</div>
                    </TooltipHost>
                    <TooltipHost content="Produce detailed ai metadata for each segment of the document for ai search retrieval." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Perform per Page Meta Analysis"
                            disabled={false}
                            checked={editedSpace.perchunkmeta == 1 ? true : false}
                            onChange={(_, checked) => handleChange("perchunkmeta", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Perform detailed content structuring for all document content (slower & costlier). Intended for Process Automation & Automated Content Processing</div>
                    </TooltipHost>
                    <TooltipHost content="Enhance images. Applies to actual images, as well as scanned documents. Improves readability, slows down ingestion." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Enhance Images"
                            disabled={false}
                            checked={editedSpace.enhance_img == 1 ? true : false}
                            onChange={(_, checked) => handleChange("enhance_img", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Apply image enhancement techniques to improve readability and comprehension for AI.</div>
                    </TooltipHost>
                    <TooltipHost content="Produce Content List for content. For some types of documents, this can take some time." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Produce Content List"
                            disabled={false}
                            checked={editedSpace.pdfcontentlist == 1}
                            onChange={(_, checked) => handleChange("pdfcontentlist", checked ? 1 : 0)}
                        />
                        <div className={styles.contentOptSubTitleInfo}>Produce document Content List.</div>
                    </TooltipHost>

                    {(selectedGroup.assistanttype == assistantCodes.anya || selectedGroup.assistanttype == assistantCodes.aas || selectedGroup.assistanttype == assistantCodes.pps) && (
                        <div>
                            <div className={styles.custLabelMrgn}>
                                <span>AI Search Indexing for V1 Chat Assistants</span>
                            </div>
                            <TooltipHost content={toolTipConfTexts["maxsectionlength"]} directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Slider
                                    styles={sliderStyles}
                                    label="Document Section Length"
                                    min={500}
                                    max={15000}
                                    step={100}
                                    defaultValue={editedSpace.maxsectionlength}
                                    showValue
                                    onChange={value => handleChange("maxsectionlength", value)}
                                />
                            </TooltipHost>
                            <TooltipHost content="Length of content in characters added at the start and end of each section, to help with keeping context between sections (Requres Reindexing)" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Slider
                                    styles={sliderStyles}
                                    label="Section Overlap"
                                    min={100}
                                    max={500}
                                    step={10}
                                    defaultValue={editedSpace.sectionoverlap}
                                    showValue
                                    onChange={value => handleChange("sectionoverlap", value)}
                                />
                            </TooltipHost>
                            <TooltipHost content="Use Microsoft Azure OpenAI Service hosted on the Microsoft Cloud (contact for more options like OpenAI, Claude, Self-Hosted Llamma, others)" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Checkbox
                                    className={styles.editSpaceCheckbox}
                                    label="Use Microsoft Azure OpenAI Implementation"
                                    disabled={true}
                                    checked={editedSpace.isintegrateddata === 1}
                                    onChange={(_, checked) => handleChange("isintegrateddata", checked ? 1 : 0)}
                                />
                            </TooltipHost>
                        </div>
                    )}

                </Stack>
                <Stack styles={stackStyles}>
                    <div className={styles.editFormSectionTitle}>
                        <span>Sharing Options</span>
                    </div>

                    <div className={styles.custLabel}>
                        <span>Share Space with Link (applies immediately)</span>
                    </div>
                    <TooltipHost content="Space will be accessible through a special link to anybody with the link. Options for link sharing will become available to the Contributors and Owners through the Sharing Icon in the Space" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <Checkbox
                            className={styles.editSpaceCheckbox}
                            label="Allow Space to be accessed with Code"
                            checked={editedSpace.sharewithcode}
                            onChange={(_, newValue) => handleChange("sharewithcode", newValue || "")}
                        />
                    </TooltipHost>
                    {editedSpace.sharewithcode == true ? (
                        <div className={styles.editSpaceShareCode}>
                            <span>
                                <div className={styles.redeemCodeBox}>
                                    {shareURL}
                                    <button className={styles.copyRedeemCodeButton} onClick={e => copyToClipboard(shareURL)}>
                                        Copy
                                    </button>
                                </div>
                            </span>
                            <TooltipHost content="Don't require user to signup/login to access the Space (Quest Access)" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <Checkbox
                                    className={styles.editAnonSpaceCheckbox}
                                    label="Get Anonymous Link"
                                    disabled={false}
                                    checked={anonSharelink}
                                    onChange={(_, checked) => handleAnonChange(checked || false)}
                                />
                            </TooltipHost>
                            <br />
                        </div>
                    ) : null}

                    <div className={styles.custLabelMrgn}>
                        <span>Embed in Web Sites [Beta]</span>
                    </div>
                    <span>
                        <div className={styles.redeemCodeBox}>
                            To add Kognify as an expandable icon at the bottom right of your website page, put the generated code at the bottom of your page's
                            HTML after the closing body tag.
                            <button
                                className={styles.copyRedeemCodeButton}
                                onClick={e =>
                                    copyToClipboard(
                                        chatbotScript(
                                            activeComp.companyid,
                                            selectedGroup.selectionId,
                                            selectedGroup.selectionText,
                                            false,
                                            selectedGroup.assistanttype == assistantCodes.prd
                                        )
                                    )
                                }
                            >
                                Get Script Code (requiring login)
                            </button>
                            <button
                                className={`${editedSpace.sharewithcode == true ? styles.copyRedeemCodeButton : styles.copyRedeemCodeButtonDisabled}`}
                                onClick={e =>
                                    copyToClipboard(
                                        chatbotScript(
                                            activeComp.companyid,
                                            selectedGroup.selectionId,
                                            selectedGroup.selectionText,
                                            true,
                                            selectedGroup.assistanttype == assistantCodes.prd
                                        )
                                    )
                                }
                                disabled={editedSpace.sharewithcode == false}
                                title={`${editedSpace.sharewithcode == true ? "" : "Enable Share with Code to get this code"}`}
                            >
                                Get Script Code (quest)
                            </button>
                        </div>
                    </span>
                    <span>
                        <div className={styles.redeemCodeBox}>
                            To embed Kognify as an iFrame window
                            <button
                                className={styles.copyRedeemCodeButton}
                                onClick={e =>
                                    copyToClipboard(chatbotEmbed(activeComp.companyid, selectedGroup.selectionId, selectedGroup.selectionText, false))
                                }
                            >
                                Get Embed Code (requiring login)
                            </button>
                            <button
                                className={`${editedSpace.sharewithcode == true ? styles.copyRedeemCodeButton : styles.copyRedeemCodeButtonDisabled}`}
                                onClick={e =>
                                    copyToClipboard(chatbotEmbed(activeComp.companyid, selectedGroup.selectionId, selectedGroup.selectionText, false))
                                }
                                disabled={editedSpace.sharewithcode == false}
                                title={`${editedSpace.sharewithcode == true ? "" : "Enable Share with Code to get this code"}`}
                            >
                                Get Embed Code (quest)
                            </button>
                        </div>
                    </span>
                </Stack>
                <Stack styles={stackStyles}>
                    {selectedGroup.permissionlevel >= 3 && (
                        <div className={styles.editFormSectionTitle}>
                            <span>Integration</span>
                        </div>
                    )}
                    {selectedGroup.permissionlevel >= 3 && (
                        <div className={styles.custLabel}>
                            <span>API</span>
                        </div>
                    )}

                    {selectedGroup.permissionlevel >= 3 && (
                        <div className={styles.userIdCont}>
                            <div className={styles.userIdLabel}>ID</div>
                            <div className={styles.useridText} onClick={() => copyToClipboard(loggedInUser.userId)}>
                                {loggedInUser.userId}
                            </div>
                        </div>
                    )}

                    {selectedGroup.permissionlevel >= 3 && (
                        <div className={styles.apikcontained}>
                            To access this space through API
                            <button className={styles.copyRedeemCodeButton} onClick={e => qGetK()}>
                                Show API Key
                            </button>
                            {apiKState && (
                                <div className={styles.apikeystring} title="Copy" onClick={() => copyToClipboard(apiKState)}>
                                    {apiKState}
                                </div>
                            )}
                        </div>
                    )}
                    {selectedGroup.permissionlevel >= 3 && selectedGroup.assistanttype == assistantCodes.cca && (
                        <div>
                            <div className={styles.custLabel}>
                                <span>Line Integration</span>
                            </div>
                            <TooltipHost content="Twilio Account SID" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textFieldStyles}
                                    label="Account SID"
                                    placeholder="Twilio Account SID"
                                    value={editedSpace.line_config.account_sid}
                                    onChange={(_, newValue) => handleLineChange("account_sid", { account_sid: newValue })}
                                />
                            </TooltipHost>
                            <TooltipHost content="From ID (unique per Assistant) - Twilio SIP Credential ID for Telephone Line Integration" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textFieldStyles}
                                    label="From ID (unique per Assistant)"
                                    placeholder="From ID"
                                    value={editedSpace.line_config.from}
                                    onChange={(_, newValue) => handleLineChange("from", { from: newValue })}
                                />
                            </TooltipHost>
                            <TooltipHost content="Twilio TwiLM App ID (unique per Assistant) for web integration" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textFieldStyles}
                                    label="TwiLM App ID (unique per Assistant)"
                                    placeholder="TwiLM App ID"
                                    value={editedSpace.line_config.twi_lmapp}
                                    onChange={(_, newValue) => handleLineChange("twi_lmapp", { twi_lmapp: newValue })}
                                />
                            </TooltipHost>
                            <TooltipHost content="From Number" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                                <TextField
                                    className={styles.spaceTextFieldsStack}
                                    styles={textFieldStyles}
                                    label="From Number"
                                    placeholder="From Number"
                                    value={editedSpace.line_config.from_number || ""}
                                    onChange={(_, newValue) => handleLineChange("from_number", { from_number: newValue })}
                                />
                            </TooltipHost>
                        </div>
                    )}
                </Stack>
                <div className={styles.modalBottomButsFixed}>
                    <IconButton className={styles.delSpaceButtonsBackground} iconProps={{ iconName: "Delete" }} onClick={handleDelete} />
                    <PrimaryButton
                        text="Save"
                        onClick={() => {handleSave({})}}
                        disabled={isLoadingAPI}
                        className={styles.saveSpaceButton}
                        styles={invFieldConfMainSaveButStyles}
                    />
                </div>
            </ThemeProvider>
        </div>
    );
};

export default SpaceEditForm;
